<template>
  <LoadingSpinner :loading='loading'> </LoadingSpinner>

  <div class="container mt-4 mb-4">
    <h3>Merchants</h3>
    <div class="mt-2" style="font-style: italic; color:var(--bo-muted)">
      <span>Alle restauranter (firma) registrert i systemet.</span>
    </div>

    <div class="mt-3 mb-3 d-flex align-items-end flex-wrap bo_merchants-filters" style="gap:1rem">
      <div>
        <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Søk</label>
        <input class="form-control" style="max-width:340px" type="text" v-model="search"
               placeholder="Navn, org.nr, telefon…" />
      </div>
      <div>
        <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Registrert fra</label>
        <Datepicker v-model="fromDate" :locale="nbLocale" inputFormat="dd.MM.yyyy" placeholder="dd.mm.åååå" class="form-control" />
      </div>
      <div>
        <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Registrert til</label>
        <Datepicker v-model="toDate" :locale="nbLocale" inputFormat="dd.MM.yyyy" placeholder="dd.mm.åååå" class="form-control" />
      </div>
      <div>
        <button class="btn btn-outline-secondary" @click="clearFilters"
                :disabled="!search && !fromDate && !toDate">Nullstill</button>
      </div>
      <div class="ms-auto" style="color:var(--bo-muted); padding-bottom:0.4rem">{{ filteredMerchants.length }} av {{ merchants.length }}</div>
    </div>

    <div v-if="!loading && merchants.length === 0" style="color:var(--bo-muted)">
      Ingen merchants funnet.
    </div>

    <!-- The header row IS the sort control on desktop, but it is hidden once rows become cards — so the
         phone gets an equivalent it can actually reach, rather than losing sorting entirely. -->
    <div v-if="hasMerchants" class="bo_sort-mobile mb-3">
      <label class="form-label mb-1" style="font-size:0.85rem; color:var(--bo-muted)">Sorter etter</label>
      <div class="d-flex" style="gap:0.5rem">
        <select class="form-select" v-model="sortKey">
          <option v-for="col in columns" :key="col.key" :value="col.key">{{ col.label }}</option>
        </select>
        <button type="button" class="btn btn-outline-secondary text-nowrap" @click="sortAsc = !sortAsc">
          <font-awesome-icon :icon="['fas', sortAsc ? 'sort-up' : 'sort-down']" />
          {{ sortAsc ? 'Stigende' : 'Synkende' }}
        </button>
      </div>
    </div>

    <table v-if="hasMerchants" class="table table-hover align-middle bo_merchants">
      <thead>
        <tr>
          <!-- A BUTTON inside the th, not a click handler on the th itself: a th cannot take keyboard
               focus, so the whole table would be sortable by mouse only. aria-sort tells a screen
               reader which column is ordering the table and which way. -->
          <th v-for="col in columns" :key="col.key" :aria-sort="ariaSort(col.key)">
            <button type="button" class="bo_sort-btn" @click="sortBy(col.key)"
                    :title="'Sorter etter ' + col.label">
              {{ col.label }}
              <font-awesome-icon :icon="['fas', sortIcon(col.key)]" class="bo_sort-icon"
                                 :class="{ 'bo_sort-icon--active': sortKey === col.key }" />
            </button>
          </th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="m in sortedMerchants" :key="m.id">
          <td data-label="Navn">{{ m.name }}</td>
          <td data-label="Org.nr">{{ m.vatNumber }}</td>
          <td data-label="Merchant account"><code>{{ m.merchant }}</code></td>
          <td data-label="Adresse">{{ formatAddress(m) }}</td>
          <td data-label="E-post"><a v-if="m.email" :href="'mailto:' + m.email">{{ m.email }}</a></td>
          <td data-label="Telefon">{{ m.phone }}</td>
          <td data-label="Registrert">{{ formatDate(m.createdDateTime) }}</td>
          <!-- One row, never wrapping: the cell is narrow and the two buttons stacked ragged otherwise.
               Tooltips live on the wrapper spans because a disabled button gets pointer-events:none
               (Bootstrap), so its own title never shows — the span receives the hover instead. -->
          <td class="text-end text-nowrap bo_merchants-actions-cell">
            <!-- The flex row is a DIV inside the cell, never the cell itself: display:flex on a <td>
                 takes it out of the table layout, so it stops being a table-cell and renders outside the
                 table box — the row border and hover highlight then stop short of the buttons. -->
            <div class="bo_merchant-actions">
              <span :title="renameBlockedReason(m) || 'Endre merchant-slug'">
                <button class="btn btn-sm btn-outline-danger"
                        :disabled="!!renameBlockedReason(m)"
                        @click="openRename(m)">
                  Endre slug
                </button>
              </span>
              <span title="Skriv Storefront-stilarkene til S3. Nye merchanter får dem automatisk ved onboarding; dette er reparasjonen for en som mangler dem.">
                <button class="btn btn-sm btn-outline-danger"
                        :disabled="cssBusy === m.merchant"
                        @click="seedCss(m)">
                  {{ cssBusy === m.merchant ? 'Skriver…' : 'Stilark' }}
                </button>
              </span>
              <span :title="(m.orderCount || 0) > 0 ? 'Merchant has order data.' : 'Slett merchant'">
                <button class="btn btn-sm btn-outline-danger"
                        :disabled="(m.orderCount || 0) > 0"
                        @click="openDelete(m)">
                  Slett
                </button>
              </span>
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>

  <popup-modal ref="renameModal">
    <h3>Endre merchant-slug</h3>
    <div class="mt-3 mb-2" v-if="renameTarget">
      Endrer sluggen for <strong>{{ renameTarget.name }}</strong> fra
      <code>{{ renameTarget.merchant }}</code> til den nye verdien. Alle data flyttes med:
      firma, avdelinger, brukere, meny, innstillinger, nettside-tema, innloggingsbrukernavnet og
      Sync Gateway-brukeren med kanalen sin.
    </div>
    <div class="mb-2" style="color:#8A6800" v-if="renameTarget">
      <strong>NB:</strong> merchanten logger inn med sluggen som brukernavn, så brukernavnet endres også.
      Passordet er uendret. Sluggen er også <b>subdomenet</b> til nettbutikken, så adressen endres fra
      <code>{{ renameTarget.merchant }}.tastyorder.no</code> til
      <code>{{ (newSlug || 'ny-slug') }}.tastyorder.no</code> — gamle lenker og trykte QR-koder slutter å virke.
    </div>
    <input class="form-control" type="text" v-model="newSlug" placeholder="f.eks. mollapizza"
           @keyup.enter="confirmRename" />
    <div class="mt-1" style="color:var(--bo-muted); font-size:0.85rem">
      Kun små bokstaver a–z. Ingen tall, mellomrom, bindestrek eller æ/ø/å.
    </div>
    <p v-if="renameError" class="text-danger mt-2 mb-0">{{ renameError }}</p>
    <div class="mt-3" style="display:flex; justify-content:flex-end; gap:1rem; border-top:1px solid var(--bo-muted-2); padding-top:15px">
      <button class="btn btn-outline-secondary" @click="cancelRename" :disabled="renaming">Avbryt</button>
      <button class="btn btn-primary" @click="confirmRename" :disabled="renaming || !newSlugValid">
        {{ renaming ? 'Endrer…' : 'Endre slug' }}
      </button>
    </div>
  </popup-modal>

  <popup-modal ref="deleteModal">
    <h3>Slett merchant</h3>
    <div class="mt-3 mb-2" v-if="deleteTarget">
      Dette sletter <strong>{{ deleteTarget.name }}</strong> og <em>alle</em> tilhørende data
      (firma, avdelinger, brukere, meny, innstillinger og Sync Gateway-bruker), samt merchantens mappe
      på S3 — <strong>produktbilder og stilark</strong>, som ikke finnes noe annet sted.
      Handlingen kan ikke angres.
    </div>
    <div class="mb-2" style="color:var(--bo-muted)" v-if="deleteTarget">
      Skriv inn merchant-kontoen <strong>{{ deleteTarget.merchant }}</strong> for å bekrefte:
    </div>
    <input class="form-control" type="text" v-model="deleteConfirmText"
           :placeholder="deleteTarget ? deleteTarget.merchant : ''" @keyup.enter="confirmDelete" />
    <p v-if="deleteError" class="text-danger mt-2 mb-0">{{ deleteError }}</p>
    <div class="mt-3" style="display:flex; justify-content:flex-end; gap:1rem; border-top:1px solid var(--bo-muted-2); padding-top:15px">
      <button class="btn btn-outline-secondary" @click="cancelDelete" :disabled="deleting">Avbryt</button>
      <button class="btn btn-danger" @click="confirmDelete" :disabled="deleting || !deleteConfirmMatches">
        {{ deleting ? 'Sletter…' : 'Slett' }}
      </button>
    </div>
  </popup-modal>

  <ConfirmDialogue ref="confirmDialog" />
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import MerchantService from "@/services/merchant/MerchantService";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import Datepicker from '@/components/other/ToggleDatepicker.vue';
import PopupModal from "@/components/other/PopupModal";
import { nb } from "date-fns/locale";

export default {
  name: "Merchants",
  components: { LoadingSpinner, Datepicker, PopupModal, ConfirmDialogue},
  data() {
    return {
      loading: true,
      merchants: [],
      search: "",
      //Label and sort key in one place, so a column cannot end up sorting by something other than what
      //it shows. 'address' is derived (formatAddress), the rest are fields on the merchant.
      columns: [
        { key: "name",            label: "Navn" },
        { key: "vatNumber",       label: "Org.nr" },
        { key: "merchant",        label: "Merchant account" },
        { key: "address",         label: "Adresse" },
        { key: "email",           label: "E-post" },
        { key: "phone",           label: "Telefon" },
        { key: "createdDateTime", label: "Registrert" }
      ],
      //Newest registration first. This is an operations console, and the merchants worth looking at are
      //almost always the ones who just signed up — whether onboarding finished, whether the welcome mail
      //went out, whether they need a call. Alphabetical put those at whatever letter they happened to
      //start with.
      //
      //The backend returns ORDER BY name ASC, but sortedMerchants re-sorts on every render, so the
      //header indicator still matches what is on screen from the first paint.
      sortKey: "createdDateTime",
      sortAsc: false,
      fromDate: null,
      toDate: null,
      deleteTarget: null,
      deleteConfirmText: "",
      deleting: false,
      deleteError: "",
      renameTarget: null,
      cssBusy: null,
      newSlug: "",
      renaming: false,
      renameError: ""
    };
  },
  computed: {
    nbLocale() {
      return nb;
    },
    filteredMerchants() {
      const q = this.search.trim().toLowerCase();
      // Datepicker binds Date objects; normalise to a local YYYY-MM-DD key for comparison.
      const fromKey = this.fromDate ? this.localDateKey(this.fromDate) : "";
      const toKey = this.toDate ? this.localDateKey(this.toDate) : "";
      return this.merchants.filter((m) => {
        if (q) {
          const hit = [m.name, m.vatNumber, m.merchant, m.city, m.email, m.phone]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(q));
          if (!hit) return false;
        }
        if (fromKey || toKey) {
          const day = this.localDateKey(m.createdDateTime); // YYYY-MM-DD, local — matches the displayed date
          if (!day) return false;
          if (fromKey && day < fromKey) return false;
          if (toKey && day > toKey) return false;
        }
        return true;
      });
    },
    hasMerchants() {
      return !this.loading && this.merchants.length > 0;
    },
    sortedMerchants() {
      //Sort a COPY: filteredMerchants is a computed, and Array.prototype.sort mutates in place.
      const list = [...this.filteredMerchants];
      if (!this.sortKey) {
        return list;
      }
      const dir = this.sortAsc ? 1 : -1;
      const isDate = this.sortKey === "createdDateTime";
      return list.sort((a, b) => {
        const av = this.sortValue(a, this.sortKey);
        const bv = this.sortValue(b, this.sortKey);
        const aEmpty = av === undefined || av === null || String(av).trim() === "";
        const bEmpty = bv === undefined || bv === null || String(bv).trim() === "";
        //Blanks sink in BOTH directions. Sorting them as if they were the smallest value fills the top
        //of the table with empty rows the moment you reverse a column, burying what you are looking for.
        if (aEmpty || bEmpty) {
          return aEmpty && bEmpty ? 0 : (aEmpty ? 1 : -1);
        }
        if (isDate) {
          //ISO 8601, so a plain string compare IS chronological — no Date parsing, no timezone.
          return (String(av) < String(bv) ? -1 : String(av) > String(bv) ? 1 : 0) * dir;
        }
        //'nb' so Æ/Ø/Å order after Z the way a Norwegian list is read, and numeric so "Kafé 2" comes
        //before "Kafé 10" rather than after it.
        return String(av).localeCompare(String(bv), "nb", { numeric: true, sensitivity: "base" }) * dir;
      });
    },
    newSlugValid() {
      // Same shape RegisterMerchant.slugify produces, and what the server enforces.
      const slug = (this.newSlug || "").trim().toLowerCase();
      return /^[a-z]+$/.test(slug) && slug.length <= 40
          && (!this.renameTarget || slug !== this.renameTarget.merchant);
    },
    deleteConfirmMatches() {
      if (!this.deleteTarget) return false;
      // Accept either the merchant account (slug, shown in the table) or the company name.
      const typed = this.deleteConfirmText.trim().toLowerCase();
      const slug = (this.deleteTarget.merchant || "").trim().toLowerCase();
      const name = (this.deleteTarget.name || "").trim().toLowerCase();
      return typed.length > 0 && (typed === slug || typed === name);
    }
  },
  mounted() {
    MerchantService.listAllMerchants().then(
      (response) => {
        this.merchants = Array.isArray(response.data) ? response.data : [];
        this.loading = false;
      },
      (error) => {
        console.log(error);
        this.loading = false;
      }
    );
  },
  methods: {
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
        return;
      }
      this.sortKey = key;
      //Newest first is what a date column is opened for; A-Z for everything else.
      this.sortAsc = key !== "createdDateTime";
    },
    //Read through the SAME helper the cell renders with, so the order matches what is on screen.
    sortValue(m, key) {
      return key === "address" ? this.formatAddress(m) : m[key];
    },
    sortIcon(key) {
      if (this.sortKey !== key) {
        return "sort";
      }
      return this.sortAsc ? "sort-up" : "sort-down";
    },
    ariaSort(key) {
      if (this.sortKey !== key) {
        return "none";
      }
      return this.sortAsc ? "ascending" : "descending";
    },
    formatAddress(m) {
      const line2 = [m.zipCode, m.city].filter(Boolean).join(" ");
      return [m.street, line2].filter(Boolean).join(", ");
    },
    formatDate(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return iso;
      return (
        d.toLocaleDateString("nb-NO") +
        " " +
        d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" })
      );
    },
    // Local YYYY-MM-DD for the registration date, so the date filter matches the date shown in the table.
    localDateKey(iso) {
      if (!iso) return "";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return "";
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },
    clearFilters() {
      this.search = "";
      this.fromDate = null;
      this.toDate = null;
    },
    /**
     * Why a rename is refused, or "" when it is allowed. Orders mean printed QR codes point at the old
     * path; terminals mean devices whose local database is named after the slug would be orphaned.
     */
    renameBlockedReason(m) {
      if ((m.orderCount || 0) > 0) {
        return "Merchanten har ordrer — trykte QR-koder peker på den gamle adressen.";
      }
      if ((m.terminalCount || 0) > 0) {
        return "Merchanten har registrerte terminaler — de ville mistet sin lokale database.";
      }
      return "";
    },
    openRename(m) {
      if (this.renameBlockedReason(m)) return;
      this.renameTarget = m;
      this.newSlug = "";
      this.renameError = "";
      this.$refs.renameModal.open();
    },
    cancelRename() {
      this.$refs.renameModal.close();
      this.renameTarget = null;
      this.newSlug = "";
      this.renameError = "";
    },
    confirmRename() {
      if (!this.newSlugValid || this.renaming) return;
      const target = this.renameTarget;
      const slug = this.newSlug.trim().toLowerCase();
      this.renaming = true;
      this.renameError = "";
      MerchantService.renameMerchant(target.merchant, slug).then(
        () => {
          target.merchant = slug; // reflect it in the table without a reload
          this.renaming = false;
          this.$refs.renameModal.close();
          this.renameTarget = null;
          this.newSlug = "";
        },
        (error) => {
          this.renaming = false;
          const data = error && error.response ? error.response.data : null;
          this.renameError = (data && data.error) || "Kunne ikke endre slug. Prøv igjen.";
        }
      );
    },
    /**
     * Writes the Storefront stylesheets for an existing merchant.
     *
     * Two dialogues on purpose. The first is informational — the caller cannot see the bucket, so they
     * are told what will be written before anything is. The second appears ONLY when the server answers
     * 409, i.e. the merchant already has stylesheets: those may be hand-edited and S3 is the only copy,
     * so replacing them is data loss and has to be a separate, explicit yes rather than a confirmation
     * the operator clicks through on the way to the first one.
     */
    async seedCss(m) {
      if (this.cssBusy) return;
      const ok = await this.$refs.confirmDialog.show({
        title: "Skriv stilark",
        message: `Skrive Storefront-stilarkene til S3 for <code>${m.merchant}</code>? ` +
          "Fargen hentes fra merchantens eget tema. Nye merchanter får dette automatisk ved onboarding.",
        okButton: "Skriv", cancelButton: "Avbryt"
      });
      if (!ok) return;
      this.cssBusy = m.merchant;
      MerchantService.seedStorefrontCss(m.merchant, false).then(
        async (response) => {
          this.cssBusy = null;
          const d = response.data || {};
          await this.$refs.confirmDialog.show({
            title: "Stilark skrevet",
            message: `${d.filesWritten} filer skrevet for <code>${m.merchant}</code> med farge ` +
              `<code>${d.accentColor}</code>.`,
            okButton: "Lukk"
          });
        },
        async (error) => {
          this.cssBusy = null;
          const status = error && error.response ? error.response.status : 0;
          const data = error && error.response ? error.response.data : null;
          const message = (data && data.error) || "Kunne ikke skrive stilarkene.";
          if (status !== 409) {
            await this.$refs.confirmDialog.show({ title: "Feilet", message, okButton: "Lukk" });
            return;
          }
          const overwrite = await this.$refs.confirmDialog.show({
            title: "Erstatte eksisterende stilark?",
            message: message + "<br><br><strong>Dette kan ikke angres</strong> — filene på S3 er eneste kopi.",
            okButton: "Erstatt", cancelButton: "Avbryt",
            requireText: m.merchant
          });
          if (!overwrite) return;
          this.cssBusy = m.merchant;
          MerchantService.seedStorefrontCss(m.merchant, true).then(
            async (response) => {
              this.cssBusy = null;
              const d = response.data || {};
              await this.$refs.confirmDialog.show({
                title: "Stilark erstattet",
                message: `${d.filesWritten} filer skrevet for <code>${m.merchant}</code> med farge ` +
                  `<code>${d.accentColor}</code>.`,
                okButton: "Lukk"
              });
            },
            async (err) => {
              this.cssBusy = null;
              const d2 = err && err.response ? err.response.data : null;
              await this.$refs.confirmDialog.show({
                title: "Feilet",
                message: (d2 && d2.error) || "Kunne ikke skrive stilarkene.",
                okButton: "Lukk"
              });
            }
          );
        }
      );
    },
    openDelete(m) {
      if ((m.orderCount || 0) > 0) return;
      this.deleteTarget = m;
      this.deleteConfirmText = "";
      this.deleteError = "";
      this.$refs.deleteModal.open();
    },
    cancelDelete() {
      this.$refs.deleteModal.close();
      this.deleteTarget = null;
      this.deleteConfirmText = "";
      this.deleteError = "";
    },
    confirmDelete() {
      if (!this.deleteConfirmMatches || this.deleting) return;
      const target = this.deleteTarget;
      this.deleting = true;
      this.deleteError = "";
      MerchantService.deleteMerchant(target.merchant).then(
        () => {
          this.merchants = this.merchants.filter((x) => x.id !== target.id);
          this.deleting = false;
          this.$refs.deleteModal.close();
          this.deleteTarget = null;
          this.deleteConfirmText = "";
        },
        (error) => {
          this.deleting = false;
          const data = error && error.response ? error.response.data : null;
          this.deleteError = (data && data.error) || "Kunne ikke slette. Prøv igjen.";
        }
      );
    }
  }
};
</script>

<style scoped>
/* A header that sorts has to look and behave like a control, but must not look like a form button in
   the middle of a table head — so it is a real <button> stripped back to the th's own typography. */
.bo_sort-btn {
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  white-space: nowrap;
}

.bo_sort-btn:hover {
  color: var(--bo-ink);
}

/* Every column shows an icon, so the head does not reflow when the sorted column changes. The inactive
   ones are faint: visible enough to say "this sorts", quiet enough not to compete with the active one. */
.bo_sort-icon {
  font-size: 0.8em;
  opacity: 0.25;
}

.bo_sort-icon--active {
  opacity: 1;
}

/* Keep the actions on one line and the same size, so the column reads as one button group rather than
   a ragged stack. All three carry the same red outline: each rewrites or removes something belonging to
   a whole tenant, and none is something to click casually. */
.bo_merchant-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.5rem;
}

.bo_merchant-actions .btn {
  min-width: 6.5rem;
}

/* Bootstrap only fades an outline button when it is disabled, so it stays recognisably red — which
   reads as "dangerous" when the point is that it cannot be used at all. Grey it out properly instead,
   at full opacity so it looks deliberately inactive rather than washed out. */
.bo_merchant-actions .btn:disabled,
.bo_merchant-actions .btn.disabled {
  color: #adb5bd;
  border-color: var(--bo-line);
  background-color: transparent;
  opacity: 1;
}

/* The mobile sort control replaces the header row, which the card layout hides. */
.bo_sort-mobile {
  display: none;
}

/* ---- Phone: the table stops being a table -----------------------------------------------------
   Eight columns cannot be read on a phone. Each row becomes a card, every value keeps the column
   name it lost when the header went away (td::before + data-label), and the name leads as the title.
   Same treatment as the device list on /mdm, so the two system pages behave alike. */
@media (max-width: 767.98px) {
  .bo_sort-mobile {
    display: block;
  }

  .bo_merchants thead {
    display: none;
  }

  .bo_merchants,
  .bo_merchants tbody,
  .bo_merchants tr,
  .bo_merchants td {
    display: block;
    width: 100%;
  }

  .bo_merchants tr {
    border: 1px solid var(--bo-line);
    border-radius: 10px;
    margin-bottom: 0.75rem;
    padding: 0.6rem 0.75rem;
    background: var(--bo-surface);
  }

  /* Bootstrap draws a line under every cell; as cards those would read as stripes. */
  .bo_merchants td {
    border: none !important;
    padding: 0.2rem 0 !important;
    display: flex;
    align-items: baseline;
    gap: 0.6rem;
    text-align: left !important;
    white-space: normal !important;
  }

  .bo_merchants td::before {
    content: attr(data-label);
    flex: 0 0 8rem;
    color: var(--bo-muted);
    font-size: 0.78rem;
    font-weight: 600;
  }

  /* The name is the card's title, so it carries no label and reads larger than the detail below it. */
  .bo_merchants td[data-label="Navn"] {
    font-size: 1.02rem;
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  .bo_merchants td[data-label="Navn"]::before {
    content: none;
  }

  /* An email can be longer than the screen; let it break rather than widen the card. */
  .bo_merchants td[data-label="E-post"] a {
    word-break: break-all;
  }

  .bo_merchants td.bo_merchants-actions-cell {
    display: block;
    margin-top: 0.6rem;
    padding-top: 0.6rem !important;
    border-top: 1px solid #f0f0f2 !important;
  }

  .bo_merchants td.bo_merchants-actions-cell::before {
    content: none;
  }

  /* Three fixed-width buttons overflow a narrow card, and a 31px-high button is not a touch target.
     Share the row instead, at the 44px minimum. */
  .bo_merchant-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  .bo_merchant-actions > span {
    flex: 1 1 auto;
  }

  .bo_merchant-actions .btn {
    width: 100%;
    min-width: 0;
    min-height: 44px;
  }

  /* The filter bar is a flex row of fixed-width fields; stacked full-width they stay usable. */
  .bo_merchants-filters > div {
    width: 100%;
  }

  .bo_merchants-filters .form-control,
  .bo_merchants-filters .btn {
    max-width: none !important;
    width: 100%;
  }

  .bo_merchants-filters .btn {
    min-height: 44px;
  }

  .bo_merchants-filters .ms-auto {
    margin-left: 0 !important;
    padding-bottom: 0 !important;
  }
}
</style>
